import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronDown, Copy, Check, Link as LinkIcon, ArrowRight } from "lucide-react";
import type { Release } from "../types";
import { formatDate, formatRelativeDate, formatMarkdownForWhatsApp } from "../utils";
import toast from "react-hot-toast";
import { ChangelogMarkdown } from "./ChangelogMarkdown";

interface ChangelogCardProps {
  release: Release;
  index?: number;
  currentPage?: number;
}

const splitSummary = (summary: string) => {
  const lines = summary.split("\n").filter((l) => l.trim());
  return {
    title: lines[0] || "",
    description: lines[1] || "",
    body: summary,
  };
};

const ChangelogCard: React.FC<ChangelogCardProps> = ({
  release,
  index = 0,
  currentPage = 1,
}) => {
  const isFeatured = index === 0 && currentPage === 1;
  const [isExpanded, setIsExpanded] = useState(isFeatured);
  const [hasCopied, setHasCopied] = useState(false);
  const [hasCopiedLink, setHasCopiedLink] = useState(false);

  const { title, description, body } = splitSummary(release.summary);
  const detailUrl = `/changelog/${encodeURIComponent(release.tagName)}`;

  const handleCopy = async () => {
    const whatsappText = formatMarkdownForWhatsApp(release.summary);
    const text = `*${release.tagName}*\n\n${whatsappText}`;
    try {
      await navigator.clipboard.writeText(text);
      setHasCopied(true);
      toast.success("Copiado");
      setTimeout(() => setHasCopied(false), 2000);
    } catch {
      toast.error("Erro ao copiar");
    }
  };

  const handleShare = async () => {
    const link = `${window.location.origin}${detailUrl}`;
    try {
      await navigator.clipboard.writeText(link);
      setHasCopiedLink(true);
      toast.success("Link copiado");
      setTimeout(() => setHasCopiedLink(false), 2000);
    } catch {
      toast.error("Erro ao copiar");
    }
  };

  return (
    <article className="relative pl-8 sm:pl-10 pb-2">
      <span className="absolute left-[7px] top-0 bottom-0 w-px bg-border" aria-hidden="true" />
      <span
        className={`absolute left-0 top-[calc(0.625rem)] w-4 h-4 rounded-full bg-background border-2 transition-colors ${
          isFeatured ? "border-primary" : "border-border"
        }`}
        aria-hidden="true"
      />

      <div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
          <Link
            to={detailUrl}
            className="font-mono text-base sm:text-lg font-semibold text-foreground hover:text-primary transition-colors"
          >
            {release.tagName}
          </Link>
          <time
            dateTime={release.releaseDate}
            title={formatDate(release.releaseDate)}
            className="text-xs text-muted-foreground"
          >
            {formatRelativeDate(release.releaseDate)}
          </time>
        </div>

        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          className="text-left w-full group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-md py-0.5"
        >
          {!isExpanded ? (
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                {title && (
                  <p
                    className={`${
                      isFeatured
                        ? "font-display text-xl sm:text-2xl text-foreground leading-snug"
                        : "text-base text-foreground/90 leading-snug"
                    } line-clamp-2`}
                  >
                    {title}
                  </p>
                )}
                {description && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                    {description}
                  </p>
                )}
              </div>
              <ChevronDown
                className="w-4 h-4 text-muted-foreground group-hover:text-foreground shrink-0 mt-1.5 transition-colors"
                aria-hidden="true"
              />
            </div>
          ) : (
            <div className="flex items-start gap-3">
              {title && (
                <p
                  className={`flex-1 ${
                    isFeatured
                      ? "font-display text-xl sm:text-2xl text-foreground leading-snug"
                      : "text-base text-foreground leading-snug"
                  }`}
                >
                  {title}
                </p>
              )}
              <ChevronDown
                className="w-4 h-4 text-muted-foreground rotate-180 shrink-0 mt-1.5 transition-transform"
                aria-hidden="true"
              />
            </div>
          )}
        </button>

        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <ChangelogMarkdown className="pt-3 pb-2 text-sm">{body}</ChangelogMarkdown>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-xs">
                <Link
                  to={detailUrl}
                  className="inline-flex items-center gap-1.5 font-medium text-foreground hover:text-primary transition-colors"
                >
                  Ler em página dedicada
                  <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </Link>
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {hasCopiedLink ? (
                    <Check className="w-3.5 h-3.5" aria-hidden="true" />
                  ) : (
                    <LinkIcon className="w-3.5 h-3.5" aria-hidden="true" />
                  )}
                  {hasCopiedLink ? "Link copiado" : "Compartilhar"}
                </button>
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {hasCopied ? (
                    <Check className="w-3.5 h-3.5" aria-hidden="true" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                  )}
                  {hasCopied ? "Copiado" : "Copiar para WhatsApp"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </article>
  );
};

export default ChangelogCard;
